import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { getAvatarColorPair } from '@/common/lib/avatar-colors'
import './NotificationToast.css'

function getInitials(name: string): string {
  return name.trim()[0]?.toUpperCase() ?? '?'
}

export interface ToastItem {
  id: string
  channelId: string
  type: 'dm' | 'channel'
  senderName: string
  preview: string
  channelName: string
  otherUserId?: string
}

const TOAST_DURATION_MS = 5000
const MAX_VISIBLE = 3

interface NotificationToastStackProps {
  toasts: ToastItem[]
  onDismiss: (id: string) => void
  onActivate: (toast: ToastItem) => void
}

export function NotificationToastStack({ toasts, onDismiss, onActivate }: NotificationToastStackProps) {
  if (toasts.length === 0) return null
  const visible = toasts.slice(0, MAX_VISIBLE)
  return createPortal(
    <div className="notif-toast-stack" aria-live="polite" aria-label="Notifications">
      {visible.map((t) => (
        <NotificationToast key={t.id} toast={t} onDismiss={onDismiss} onActivate={onActivate} />
      ))}
    </div>,
    document.body,
  )
}

export function NotificationToast({
  toast,
  onDismiss,
  onActivate,
}: {
  toast: ToastItem
  onDismiss: (id: string) => void
  onActivate: (toast: ToastItem) => void
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { bg, text } = getAvatarColorPair(toast.senderName)
  const label = toast.type === 'dm' ? 'Direct Message' : `#${toast.channelName}`
  const preview = toast.preview.length > 60 ? `${toast.preview.slice(0, 60)}…` : toast.preview

  function startTimer() {
    stopTimer()
    timerRef.current = setTimeout(() => onDismiss(toast.id), TOAST_DURATION_MS)
  }
  function stopTimer() {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }

  useEffect(() => {
    startTimer()
    return stopTimer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className="notif-toast"
      role="button"
      aria-label={`Notification from ${toast.senderName}`}
      tabIndex={0}
      onClick={() => onActivate(toast)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(toast) }
      }}
      onMouseEnter={stopTimer}
      onMouseLeave={startTimer}
    >
      <div className="notif-toast-avatar" style={{ background: bg, color: text }} aria-hidden="true">
        {getInitials(toast.senderName)}
      </div>
      <div className="notif-toast-body">
        <div className="notif-toast-meta">
          <span className="notif-toast-sender">{toast.senderName}</span>
          <span className="notif-toast-label">{label}</span>
        </div>
        <div className="notif-toast-preview">{preview}</div>
      </div>
      <button
        type="button"
        className="notif-toast-close"
        onClick={(e) => { e.stopPropagation(); onDismiss(toast.id) }}
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  )
}
